import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TooltipButton from '../../../components/tooltip-button/TooltipButton';
import SortField from '../sort-field/SortField';
import generateFileName from '../../../utils/generateFileName';
import exportTextMessages from '../../../utils/exportTextMessages';
import isDefined from '../../../utils/isDefined';
import useConstructExportMessages from '../../hooks/useConstructExportMessages';
import { BUTTON_STYLES } from '../../../components/common-button/CommonButton';
import Icon from '../../../icons/Icon';
import { ICONS } from '../../../icons';

import scss from './GenericFilters.module.scss';

export const EXPORT_STATUS = {
  DISABLED: 'DISABLED',
  ESTIMATING: 'ESTIMATING',
  EXPORTING: 'EXPORTING',
};

function GenericFilters({
  projectName,
  exportContext,
  messages,
  onSort,
  sort,
  isDisabled,
  getExportMessages = (messages) => messages,
  AdditionalButtons,
}) {
  const { t } = useTranslation('genie');
  const [exportStatus, setExportStatus] = useState('');

  const exportTooltips = {
    [EXPORT_STATUS.DISABLED]: t('thereAreNoMessagesToExport'),
    [EXPORT_STATUS.ESTIMATING]: t('messagesAreStillLoadingNow'),
  };

  const constructExportMessages = useConstructExportMessages();

  const handleExportMessages = async () => {
    setExportStatus(EXPORT_STATUS.EXPORTING);

    const fileName = generateFileName({ name: projectName, context: exportContext, ext: 'txt' });
    const constructedMessages = constructExportMessages(getExportMessages([...messages].reverse()));

    await exportTextMessages(constructedMessages, { fileName });

    setExportStatus('');
  };

  const handleChangeSort = ({ sort, direction, getSortValue }) => {
    onSort({ sort, direction, getSortValue });
  };

  useEffect(() => {
    if (!messages?.length) {
      setExportStatus(EXPORT_STATUS.DISABLED);
      return;
    }

    if (messages.some(({ uncertainty }) => !isDefined(uncertainty))) {
      setExportStatus(EXPORT_STATUS.ESTIMATING);
      return;
    }

    setExportStatus('');
  }, [messages]);

  return (
    <div className={scss.wrapper}>
      <div className={scss.buttons}>
        {AdditionalButtons ? <AdditionalButtons /> : null}

        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          tooltip={exportTooltips[exportStatus]}
          onClick={handleExportMessages}
          maxWidth={180}
          disabled={!!exportStatus}
          isLoading={exportStatus === EXPORT_STATUS.EXPORTING}>
          <Icon icon={ICONS.ArrowUp} disableCommonColors />
          {t('exportMessages')}
        </TooltipButton>
      </div>

      <div className={scss.sort}>
        <SortField onSortChange={handleChangeSort} sort={sort} disabled={isDisabled} />
      </div>
    </div>
  );
}

export default GenericFilters;
